import { parseContact, parseJSONData, propertyInterestDataSchema } from '@aikoa/common'
import type { FeedbackNotification, ReceivedFeedback } from '~/data/feedback/types'
import { supabase } from '~/lib/supabase'

export async function getFeedback({
  userId,
  contactIds,
  answeredOnly = false,
}: {
  userId: string
  contactIds?: string | string[]
  answeredOnly?: boolean
}) {
  const query = supabase
    .from('feedback')
    .select(
      `*,
      notifications:feedback_notifications(method, status, sent_at),
      contact:contacts!inner(*),
      interests(id, data, created_at)
  `
    )
    .eq('user_id', userId)
    .is('deleted_at', null)
    .is('contact.deleted_at', null)
    .order('created_at', { ascending: false })

  if (answeredOnly) {
    query.not('completed_at', 'is', null)
  }

  if (contactIds) {
    query.in('contact_id', Array.isArray(contactIds) ? contactIds : [contactIds])
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching feedback:', error)
    return []
  }

  const feedbackItems: ReceivedFeedback[] = []

  for (const feedback of data ?? []) {
    const contact = parseContact({
      ...feedback.contact,
      feedback: [feedback],
    })

    const { notifications, interests, ...feedbackValues } = feedback
    const latestInterest = Array.isArray(interests)
      ? interests.sort(
          (interestA, interestB) =>
            new Date(interestB.created_at).getTime() - new Date(interestA.created_at).getTime()
        )[0]
      : null

    const parsedInterestData = parseJSONData(latestInterest?.data ?? {}, propertyInterestDataSchema)
    const answers = parsedInterestData?.answers ?? null

    feedbackItems.push({
      ...feedbackValues,
      answers,
      contact,
      interest: latestInterest
        ? {
            id: latestInterest.id,
            data: parsedInterestData ?? null,
          }
        : null,
      notifications: (notifications ?? []) as FeedbackNotification[],
    } satisfies ReceivedFeedback)
  }

  feedbackItems.sort((itemA, itemB) => {
    if (itemA.completed_at && !itemB.completed_at) {
      return -1
    }

    if (!itemA.completed_at && itemB.completed_at) {
      return 1
    }

    if (itemA.completed_at && itemB.completed_at) {
      const completedDiff =
        new Date(itemB.completed_at).getTime() - new Date(itemA.completed_at).getTime()

      if (completedDiff !== 0) {
        return completedDiff
      }
    }

    return new Date(itemB.updated_at).getTime() - new Date(itemA.updated_at).getTime()
  })

  const seenFeedbackKeys = new Set<string>()
  const deduplicatedFeedback: ReceivedFeedback[] = []

  for (const feedback of feedbackItems) {
    const dedupeKey = feedback.id

    if (!seenFeedbackKeys.has(dedupeKey)) {
      seenFeedbackKeys.add(dedupeKey)
      deduplicatedFeedback.push(feedback)
    }
  }

  return deduplicatedFeedback
}

export type LatestRespondedFeedback = {
  feedbackId: string
  contactId: string
  realtorUserId: string | null
  timingAnswer: number | null
}

export async function getLatestRespondedFeedback(
  userId: string
): Promise<LatestRespondedFeedback | null> {
  const { data, error } = await supabase
    .from('feedback')
    .select('id, contact_id, user_id, completed_at, interests(data, created_at)')
    .eq('responder_user_id', userId)
    .not('completed_at', 'is', null)
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error || !data) {
    return null
  }

  const latestInterest =
    Array.isArray(data.interests) && data.interests.length > 0
      ? data.interests.sort(
          (interestA, interestB) =>
            new Date(interestB.created_at).getTime() - new Date(interestA.created_at).getTime()
        )[0]
      : null

  const parsedInterestData = parseJSONData(latestInterest?.data ?? {}, propertyInterestDataSchema)

  const timingEntry = parsedInterestData
    ? parsedInterestData.answers.find((answer) => answer.id === 'timing')
    : undefined

  const timingAnswer = typeof timingEntry?.value === 'number' ? timingEntry.value : null

  return {
    feedbackId: data.id,
    contactId: data.contact_id,
    realtorUserId: data.user_id,
    timingAnswer,
  }
}
